import Card from "@/design-system/Card/Card";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useMediaQuery } from "usehooks-ts";

const Block = ({ className }: { className?: string }) => (
  <div
    aria-hidden
    className={cn("rounded-md bg-neutral-700/60 animate-pulse", className)}
  />
);

/** The step grid before the first steps land: two categories of cards. */
export const EvolutionsStepsSkeleton = () => (
  <div className="flex flex-col gap-4 pb-8">
    <div className="mt-6 flex flex-col gap-2">
      <Block className="h-5 w-40" />
      <div className="grid grid-cols-1 gap-4 mobile:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <StepSkeletonCard key={`large-${index}`} withPlayer />
        ))}
      </div>
    </div>
    <div className="mt-6 flex flex-col gap-2">
      <Block className="h-5 w-28" />
      <div className="grid grid-cols-1 gap-4 mobile:grid-cols-3 large-desktop:grid-cols-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <StepSkeletonCard key={`small-${index}`} />
        ))}
      </div>
    </div>
  </div>
);

const StepSkeletonCard = ({ withPlayer = false }: { withPlayer?: boolean }) => (
  <Card hoverable={false} className="flex gap-3 p-3">
    {withPlayer && <Block className="h-[150px] w-[108px] shrink-0" />}
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <Block className="h-6 w-32 bg-evolution/60" />
        <Block className="h-7 w-24 rounded-full" />
      </div>
      <div className="flex gap-1.5">
        <Block className="h-6 w-14 rounded-lg" />
        <Block className="h-6 w-16 rounded-lg" />
      </div>
      <div className="mt-auto flex flex-col gap-1.5 rounded-xl bg-background p-2.5">
        <div className="flex gap-1.5">
          <Block className="h-6 w-14" />
          <Block className="h-6 w-14" />
          <Block className="h-6 w-14" />
        </div>
        <div className="flex gap-1.5">
          <Block className="h-6 w-6" />
          <Block className="h-6 w-6" />
        </div>
      </div>
    </div>
  </Card>
);

/** A recommended chain while the solver is still thinking. */
export const ChainRecommendationSkeletonCard = () => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  return (
    <Card hoverable={false} className="flex gap-3 p-3">
      <Block className="h-[150px] w-[108px] shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1">
            {Array.from({ length: isTouchDevice ? 1 : 4 }).map((_, index) => (
              <div key={index} className="flex items-center">
                <Block className="h-6 w-24 bg-evolution/60" />
                {index < 3 && !isTouchDevice && (
                  <div className="h-0.5 w-3 bg-evolution-secondary/50" />
                )}
              </div>
            ))}
          </div>
          <Block className="h-7 w-28 rounded-full" />
        </div>
        <div className="flex gap-1.5">
          <Block className="h-6 w-14 rounded-lg" />
          <Block className="h-6 w-16 rounded-lg" />
          <Block className="h-6 w-16 rounded-lg" />
        </div>
        <div className="mt-auto flex flex-col gap-1.5 rounded-xl bg-background p-2.5">
          <div className="flex gap-1.5">
            {Array.from({ length: isTouchDevice ? 3 : 6 }).map((_, index) => (
              <Block key={index} className="h-6 w-14" />
            ))}
          </div>
          <div className="flex gap-1.5">
            <Block className="h-6 w-20" />
            <Block className="h-6 w-20" />
          </div>
          <div className="flex gap-1.5">
            <Block className="h-6 w-6" />
            <Block className="h-6 w-6" />
            <Block className="h-6 w-6" />
          </div>
        </div>
      </div>
    </Card>
  );
};
